import React, { useRef, useCallback, useEffect } from "react";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";
import { incrementBet } from "../../../../State/actions";
import {
  selectIsSpinning,
  selectUserInterfaceDisabled,
  selectSessionExpired,
  selectBet,
  selectBets,
  selectBalance,
  selectAutoPlayActive,
  selectHoldAndWinActive,
} from "../../../../State/selectors";

const ButtonIncrementBet = ({ x = 0, y = 0 }) => {
  const dispatch = useDispatch();
  const { getResponsiveConfig } = useDimensions();

  const isSpinning = useSelector(selectIsSpinning);
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const balance = useSelector(selectBalance);
  const sessionExpired = useSelector(selectSessionExpired);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  const texture = PIXI.Assets.get(ASSET_NAME.icon_popi_plus);

  const currentBetIndex = bets.indexOf(bet);
  const nextBetAmount = bets[currentBetIndex + 1];

  const canIncreaseBet =
    currentBetIndex < bets.length - 1 &&
    balance >= nextBetAmount &&
    !isSpinning &&
    !sessionExpired &&
    !autoPlayActive &&
    !userInterfaceDisabled &&
    !holdAndWinActive;

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  const handleClick = useCallback(() => {
    if (canIncreaseBet) {
      soundManager.play(SOUND_NAME.button_click);

      // Add press animation before incrementing bet
      if (buttonSpriteRef.current) {
        createButtonPressAnimation(buttonSpriteRef.current);
      }

      dispatch(incrementBet());
    }
  }, [canIncreaseBet, dispatch]);

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    if (canIncreaseBet && buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [canIncreaseBet]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, []);

  // Clean up hover effects on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
    };
  }, []);

  return (
    <Sprite
      ref={buttonSpriteRef}
      texture={texture}
      name="secondary_button_increment_bet"
      anchor={{ x: 0, y: 0.5 }}
      width={buttonConfig.width}
      height={buttonConfig.height}
      x={x}
      y={y}
      hitArea={new PIXI.Circle(buttonConfig.width / 2, 0, (buttonConfig.width / 2) * 1.4)}
      eventMode={canIncreaseBet ? "static" : "none"}
      cursor={canIncreaseBet ? "pointer" : "default"}
      alpha={canIncreaseBet ? 1 : 0.5}
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
    />
  );
};

export default ButtonIncrementBet;
